import type { Ref } from 'vue'
import { ref, watch, onMounted, onUnmounted } from 'vue'
import {
  deleteQueryParam,
  setQueryParam,
  getQueryParam,
} from '@app/utils/url-helpers'

type QueryParam = NonNullable<ReturnType<typeof getQueryParam>>

export default function useUrlStorage<T>(
  key: string,
  initialValue: T,
  serialize: (value: T) => string,
  {
    syncToUrl = true,
    deserialize,
  }: { syncToUrl?: boolean; deserialize?: (param: QueryParam) => T } = {}
): Ref<T> {
  const value = ref<T>(initialValue) as Ref<T>

  if (!syncToUrl) return value

  const pathname = window.location.pathname

  // A param the URL does not carry is the initial value, so Back onto a URL
  // without it undoes the change, such as closing a panel the URL had opened.
  const readFromUrl = (): void => {
    // Back to another page fires popstate before Turbolinks unmounts this
    // component, and the watcher would write this value into that page's URL.
    if (window.location.pathname !== pathname) return

    const param = getQueryParam(key)
    if (param) {
      value.value = deserialize ? deserialize(param) : (param as unknown as T)
    } else {
      value.value = initialValue
    }
  }
  readFromUrl()

  watch(
    value,
    (newValue) => {
      const serialized = serialize(newValue)
      if (serialized !== '') {
        setQueryParam(key, serialized, { replace: true })
      } else {
        deleteQueryParam(key, { replace: true })
      }
    },
    { deep: true }
  )

  // Handle browser back/forward
  onMounted(() => window.addEventListener('popstate', readFromUrl))
  onUnmounted(() => window.removeEventListener('popstate', readFromUrl))

  return value
}
